import { pushTo } from '@app/core/routing';
import { routeGenerator } from '@app/modules/fragments/routing/FragmentsRoutes';
import { webApi } from '@mi-front/api-clients';
import { GetFragmentDto } from '@mi-front/core';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Menu, MenuItem } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAvailableExportPlatforms } from './hooks/useAvailableExportPlatforms';

interface SocialExportPublishDropdownProps {
  fragment: GetFragmentDto;
  uploadsByPlatform: Map<string, webApi.FragmentSocialUploadItemDto[]> | undefined;
}

/**
 * Dropdown showing "Publicar en" with one option per connected platform that has no uploads yet.
 * Selecting an option navigates to the upload screen for that platform.
 */
export function SocialExportPublishDropdown({
  fragment,
  uploadsByPlatform,
}: SocialExportPublishDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const availablePlatforms = useAvailableExportPlatforms(fragment.stationId, uploadsByPlatform);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePlatformSelect = (platformId: string) => {
    const platform = availablePlatforms.find(
      (p) => p.id.toUpperCase() === platformId.toUpperCase(),
    );
    if (platform) {
      pushTo(routeGenerator.upload(fragment, platform));
    }
    handleClose();
  };

  if (availablePlatforms.length === 0) return null;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
      >
        <FormattedMessage id="fragments.socialExport.publishTo" defaultMessage="Publicar en" />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {availablePlatforms.map((platform) => (
          <MenuItem key={platform.id} onClick={() => handlePlatformSelect(platform.id)}>
            {platform.Icon({ sx: { mr: 1, fontSize: 20 }, colored: true })} {platform.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
